import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Lock } from 'lucide-react';
import { Alert, Badge, Button, Card, CardContent, ErrorState, Input, Label, LoadingState, Select, Textarea } from '@buzzbee/ui';
import { useEffect, useState } from 'react';
import { HoursEditor, LANGUAGES, ServicesEditor, normalizeServices, type Hours, type Service } from '../components/editors';
import { Logo } from '../components/common';
import { CalendarSettings } from '../components/calendar';
import { IndustryPicker, IndustryPreview, StoreConnectionForm, type PackSummary } from '../components/industry';
import { errorMessage, get, post } from '../lib/api';
import { useRouter } from '../lib/router';
import { hasFeature, useRefreshMe, useWorkspace } from '../lib/session';
import { TestCall } from './simulator';

const STEPS = ['Your industry', 'Hours, services and staff', 'Calendar', 'Phone number', 'Disclosure and language', 'Test call'];

export function OnboardingPage() {
  const ws = useWorkspace();
  const refresh = useRefreshMe();
  const { navigate } = useRouter();
  const q = useQuery({ queryKey: ['onboarding'], queryFn: () => get('/api/onboarding') });
  const [step, setStep] = useState(0);
  const [business, setBusiness] = useState({ name: '', vertical: 'general', timezone: 'America/Toronto', phone: '' });
  const [hours, setHours] = useState<Hours>({});
  const [services, setServices] = useState<Service[]>([]);
  const [staff, setStaff] = useState('');
  const [phone, setPhone] = useState({ mode: 'forward_existing', forwardingFromNumber: '', forwardingMode: 'no_answer' });
  const [voice, setVoice] = useState({ disclosure: '', greeting: '', defaultLanguage: 'en', languages: [] as string[] });
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    if (!q.data) return;
    const s = q.data.settings;
    setStep(Math.min(q.data.step, 5));
    setBusiness({ name: s.business.name, vertical: s.business.vertical, timezone: s.business.timezone, phone: s.business.phone ?? '' });
    const primary = s.locations.find((l: any) => l.id === s.primaryLocationId);
    setHours(primary?.businessHours ?? {});
    setServices(s.services);
    setStaff(s.staff.map((x: any) => x.name).join(', '));
    setPhone({ mode: 'forward_existing', forwardingFromNumber: primary?.forwardingFromNumber ?? s.business.phone ?? '', forwardingMode: primary?.forwardingMode ?? 'no_answer' });
    setVoice({ disclosure: s.voice.disclosure, greeting: s.voice.greeting ?? '', defaultLanguage: s.voice.defaultLanguage, languages: s.voice.languages });
  }, [q.data]);

  const skip = useMutation({
    mutationFn: (i: number) => post<{ step: number }>('/api/onboarding/skip', { step: i }),
    onSuccess: async (_r, i) => {
      await q.refetch();
      setStep(Math.min(5, i + 1));
    },
  });

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => post<{ step: number; completed: boolean }>('/api/onboarding/step', payload),
    onSuccess: async (r, payload) => {
      await q.refetch();
      if (payload.step === 0) await refresh();
      if (r.completed && payload.step === 5) {
        await refresh();
        navigate('/');
      } else setStep(Math.min(5, Number(payload.step) + 1));
    },
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const s = q.data.settings;
  const primary = s.locations.find((l: any) => l.id === s.primaryLocationId);
  const multilingual = hasFeature(ws, 'multilingual');
  const packs = s.packs as PackSummary[];
  const chosen = packs.find((p) => p.id === business.vertical) ?? packs[packs.length - 1]!;
  // The saved pack decides later steps (an e-commerce shop has no services or calendar).
  const books = s.books as boolean;

  const payloadFor = (i: number): Record<string, unknown> => {
    switch (i) {
      case 0:
        return { step: 0, ...business, phone: business.phone || undefined };
      case 1:
        return { step: 1, businessHours: hours, services: normalizeServices(services), staff: staff.split(',').map((n) => n.trim()).filter(Boolean).map((name) => ({ name, services: [] })) };
      case 2:
        // The calendar and booking mode save as they are chosen (components/calendar.tsx).
        return { step: 2 };
      case 3:
        return { step: 3, mode: phone.mode, forwardingFromNumber: phone.forwardingFromNumber || undefined, forwardingMode: phone.forwardingMode };
      case 4:
        return { step: 4, disclosure: voice.disclosure, greeting: voice.greeting || undefined, defaultLanguage: voice.defaultLanguage, languages: voice.languages };
      default:
        return { step: 5 };
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <Logo product={ws.brand.product} />
        <span className="text-sm text-muted-foreground">
          Step {step + 1} of {STEPS.length}
        </span>
      </div>
      <div className="flex gap-1" aria-hidden>
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-primary' : 'bg-muted'}`} />
        ))}
      </div>
      <nav aria-label="Setup steps" className="flex flex-wrap gap-2">
        {STEPS.map((label, i) => (
          <button key={label} type="button" disabled={i > q.data.step} onClick={() => setStep(i)} className={`rounded-full px-3 py-1 text-xs ${i === step ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'} disabled:opacity-50`}>
            {i < q.data.step ? <Check className="mr-1 inline size-3" /> : null}
            {label}
          </button>
        ))}
      </nav>
      <Card>
        <CardContent className="space-y-5 p-7">
          <h1 className="text-2xl font-semibold tracking-[-0.02em]">{STEPS[step]}</h1>
          {step === 0 ? (
            <div className="grid gap-4">
              <div className="space-y-1.5">
                <p className="text-sm text-muted-foreground">Pick your industry and we'll fill in everything else. You can change any of it later.</p>
                <IndustryPicker packs={packs} value={business.vertical} onChange={(vertical) => setBusiness({ ...business, vertical })} />
              </div>
              <IndustryPreview pack={chosen} />
              <div className="space-y-1.5">
                <Label htmlFor="ob-name">Business name</Label>
                <Input id="ob-name" value={business.name} onChange={(e) => setBusiness({ ...business, name: e.target.value })} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ob-tz">Time zone</Label>
                  <Select id="ob-tz" value={business.timezone} onChange={(e) => setBusiness({ ...business, timezone: e.target.value })} options={['America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax', 'America/St_Johns', 'America/Regina', 'America/New_York', 'America/Chicago', 'America/Los_Angeles'].map((z) => ({ value: z, label: z }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ob-phone">Business phone</Label>
                  <Input id="ob-phone" value={business.phone} onChange={(e) => setBusiness({ ...business, phone: e.target.value })} placeholder="+1 416 555 0100" />
                </div>
              </div>
            </div>
          ) : null}
          {step === 1 ? (
            <div className="space-y-6">
              <section className="space-y-2">
                <h2 className="font-semibold">Opening hours</h2>
                <HoursEditor value={hours} onChange={setHours} />
              </section>
              {books ? (
                <>
                  <section className="space-y-2">
                    <h2 className="font-semibold">Services the receptionist can book</h2>
                    <ServicesEditor value={services} onChange={setServices} />
                  </section>
                  <section className="space-y-2">
                    <Label htmlFor="ob-staff">Staff (comma separated, optional)</Label>
                    <Input id="ob-staff" value={staff} onChange={(e) => setStaff(e.target.value)} placeholder="Ava, Priya" />
                  </section>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Your assistant answers order and policy questions instead of booking, so there are no services to set up.</p>
              )}
            </div>
          ) : null}
          {step === 2 && s.ordersEnabled ? <StoreConnectionForm store={s.store} /> : null}
          {step === 2 && !s.ordersEnabled ? <CalendarSettings canEdit returnTo="onboarding" /> : null}
          {step === 3 ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { id: 'forward_existing', name: 'Keep my number', desc: 'Forward your existing number to your AI receptionist.' },
                  { id: 'new_number', name: 'Get a new number', desc: 'Use a new local number for the receptionist.' },
                ].map((m) => (
                  <label key={m.id} className={`flex cursor-pointer items-start gap-3 rounded-xl p-4 ring-1 ${phone.mode === m.id ? 'ring-primary' : 'ring-border'}`}>
                    <input type="radio" name="mode" checked={phone.mode === m.id} onChange={() => setPhone({ ...phone, mode: m.id })} className="mt-1" />
                    <span>
                      <span className="block font-medium">{m.name}</span>
                      <span className="block text-sm text-muted-foreground">{m.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
              {phone.mode === 'forward_existing' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="ob-fwd">Your current business number</Label>
                  <Input id="ob-fwd" value={phone.forwardingFromNumber} onChange={(e) => setPhone({ ...phone, forwardingFromNumber: e.target.value })} />
                </div>
              ) : null}
              {primary?.agentPhoneNumber ? (
                <Alert variant="success" title={`Your receptionist's number: ${primary.agentPhoneNumber}`}>
                  {phone.mode === 'forward_existing' ? 'Forward your business line to it:' : 'Share it with customers or put it on your website.'}
                </Alert>
              ) : (
                <p className="text-sm text-muted-foreground">We'll assign your receptionist's number when you continue.</p>
              )}
              {phone.mode === 'forward_existing' ? (
                <>
                  <fieldset className="grid gap-3 sm:grid-cols-2">
                    <legend className="mb-2 text-sm font-medium">Which calls should go to your receptionist?</legend>
                    {[
                      { id: 'no_answer', name: 'Only calls we miss (recommended)', desc: 'Your phone rings first; unanswered and busy calls go to the receptionist. Transfers to this number still reach your team.' },
                      { id: 'all', name: 'Every call', desc: 'The receptionist answers everything. Live transfers then need a different number, such as a cell phone.' },
                    ].map((m) => (
                      <label key={m.id} className={`flex cursor-pointer items-start gap-3 rounded-xl p-4 ring-1 ${phone.forwardingMode === m.id ? 'ring-primary' : 'ring-border'}`}>
                        <input type="radio" name="forwarding-mode" checked={phone.forwardingMode === m.id} onChange={() => setPhone({ ...phone, forwardingMode: m.id })} className="mt-1" />
                        <span>
                          <span className="block font-medium">{m.name}</span>
                          <span className="block text-sm text-muted-foreground">{m.desc}</span>
                        </span>
                      </label>
                    ))}
                  </fieldset>
                  <ul className="space-y-3 text-sm">
                    {q.data.forwarding.map((f: { line: string; noAnswer: string; all: string; cancel: string }) => {
                      const number = primary?.agentPhoneNumber ?? 'your receptionist number';
                      return (
                        <li key={f.line}>
                          <strong>{f.line}:</strong> {(phone.forwardingMode === 'all' ? f.all : f.noAnswer).replaceAll('{n}', number)}
                          <span className="block text-xs text-muted-foreground">To undo: {f.cancel}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="text-xs text-muted-foreground">If a code does not work, your phone company may need to turn on call forwarding for your line first.</p>
                </>
              ) : null}
            </div>
          ) : null}
          {step === 4 ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ob-disc">AI and recording disclosure (said at the start of every call)</Label>
                <Textarea id="ob-disc" rows={2} value={voice.disclosure} onChange={(e) => setVoice({ ...voice, disclosure: e.target.value })} />
                <p className="text-xs text-muted-foreground">Required. It must say the caller is speaking with an AI and that the call may be recorded.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ob-greet">Greeting</Label>
                <Input id="ob-greet" value={voice.greeting} onChange={(e) => setVoice({ ...voice, greeting: e.target.value })} placeholder={`Thanks for calling ${ws.name}. How can I help?`} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ob-lang">Main language</Label>
                <Select id="ob-lang" value={voice.defaultLanguage} onChange={(e) => setVoice({ ...voice, defaultLanguage: e.target.value })} options={LANGUAGES.map((l) => ({ value: l.code, label: l.name }))} />
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">
                  Also speak {multilingual ? null : <Badge variant="primary"><Lock className="size-3" /> ReceptionistBee</Badge>}
                </legend>
                <div className="flex flex-wrap gap-3">
                  {LANGUAGES.filter((l) => l.code !== voice.defaultLanguage).map((l) => (
                    <label key={l.code} className={`flex items-center gap-2 text-sm ${multilingual ? '' : 'opacity-50'}`}>
                      <input
                        type="checkbox"
                        disabled={!multilingual}
                        checked={multilingual && voice.languages.includes(l.code)}
                        onChange={(e) => setVoice({ ...voice, languages: e.target.checked ? [...voice.languages, l.code] : voice.languages.filter((x) => x !== l.code) })}
                      />
                      {l.name}
                    </label>
                  ))}
                </div>
                {!multilingual ? <p className="text-xs text-muted-foreground">Switching languages mid-call is part of ReceptionistBee.</p> : null}
              </fieldset>
            </div>
          ) : null}
          {step === 5 ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Call your receptionist right here. Try booking an appointment; it lands in your calendar marked as a test.</p>
              <TestCall onBooked={() => setBooked(true)} />
              {booked ? <Alert variant="success" title="Booking worked">Your receptionist is ready.</Alert> : null}
            </div>
          ) : null}
          {save.error ? <Alert variant="destructive">{errorMessage(save.error)}</Alert> : null}
          <div className="flex justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)}>
              Back
            </Button>
            <div className="flex gap-2">
              {step >= 1 && step <= 4 ? (
                <Button variant="outline" loading={skip.isPending} onClick={() => skip.mutate(step)}>
                  Skip for now
                </Button>
              ) : null}
              <Button
                loading={save.isPending}
                onClick={() => {
                  const p = payloadFor(step);
                  if (step === 4 && !multilingual) p.languages = [];
                  save.mutate(p);
                }}
              >
                {step === 5 ? 'Finish and go live' : 'Continue'}
              </Button>
            </div>
          </div>
          {skip.error ? <Alert variant="destructive">{errorMessage(skip.error)}</Alert> : null}
        </CardContent>
      </Card>
    </div>
  );
}
